// Renders all of an Item's logbooks (one LogbookSummary card each) plus an
// "Add Logbook" control. An Item starts with no logbooks; the user adds the
// first one here. Logbooks live on `item.logbooks.items`.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { DataCard, LogbookSummary, useStatusMessage } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import * as Database from '../../utilities/Database.mjs';
import { urlForLogbookForId } from '../../fleet-shared/Urls.mjs';
import { LOGBOOK_TYPES, defaultNameForType } from '../../fleet-shared/LogbookTypes.mjs';
import { suggestedLogbookType } from '../../fleet-shared/ItemTypes.mjs';
import { isLogOnly } from '../../fleet-shared/ItemTracking.mjs';
import { parseLogbookExport } from '../../fleet-shared/LogbookIO.mjs';
import { readFileAsText } from '../../utilities/downloadFile.mjs';
import { ensurePlaces, placesFailedNotice } from '../../utilities/places.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { extractErrorText } from '../../fleet-shared/Common.mjs';
import { makeLogbookSummaryCallbacks } from './logbookSummaryCallbacks';

const entryWord = (n) => (n === 1 ? 'entry' : 'entries');
const placeWord = (n) => (n === 1 ? 'place' : 'places');

// An import runs in two phases and the bar has to say which: the gazetteer
// rows the file's routes refer to are created first (so the entries can be
// bound to them), then the entries themselves. The places phase doesn't fire
// at all when this environment already has every name.
const PHASE_COPY = {
  places: ({ done, total }) => `Added ${done} of ${total} ${placeWord(total)}...`,
  entries: ({ done, total }) => `Imported ${done} of ${total} ${entryWord(total)}...`,
};

// Modal to pick a logbook type and name when adding a new logbook, or to import
// a whole logbook from a previously-exported JSON file. Importing creates a new
// logbook, so it belongs in the same "add" flow rather than inside an existing
// logbook.
// `itemType` only chooses which option the modal OPENS on -- a boat opens on
// the Captain's Log, an airplane on the Pilot's Logbook, everything else
// (including a typeless item) on Maintenance. All three stay listed and
// selectable for every item; nothing is filtered out. See ItemTypes.mjs.
const AddLogbookModal = ({ show, onHide, onAdd, onImport, itemType = null }) => {
  const suggested = suggestedLogbookType(itemType);
  const [logbookType, setLogbookType] = useState(suggested);
  // Track whether the user has hand-edited the name; if not, keep it synced to
  // the selected type's default so switching type updates the suggestion.
  const [name, setName] = useState(defaultNameForType(suggested));
  const [nameEdited, setNameEdited] = useState(false);
  // Set once the user picks a type by hand. Its only job is to stop the
  // suggestion effect below from overwriting that choice.
  const [typeEdited, setTypeEdited] = useState(false);
  const [busy, setBusy] = useState(false);

  // Import flow. `pendingImport` holds a validated file awaiting confirmation;
  // while it is set the modal swaps to a confirm view. `importError` surfaces a
  // bad-file message inline (the page's status is hidden behind this modal).
  // `importProgress` is { done, total, phase } once the import is under way;
  // rows are created one at a time, so a large file otherwise looks hung.
  const fileInputRef = useRef(null);
  const [pendingImport, setPendingImport] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importProgress, setImportProgress] = useState(null);

  // Follow the suggestion until the user picks a type themselves.
  //
  // Two things make a useState initialiser insufficient. The modal is always
  // mounted (it is shown via its `show` prop), so the initialiser runs once for
  // the life of the page; and the item page renders it while `item` is still
  // null. A null item yields `logbooks.length === 0`, which is the empty-state
  // branch that draws the Add Logbook button -- so the button is on screen
  // DURING the load, not after it, and someone can open this modal before
  // `itemType` has arrived. Keying only on `show` left that case stuck on
  // Maintenance for a boat.
  //
  // Hence `suggested` in the deps: a late-arriving itemType advances the
  // still-untouched dropdown. `typeEdited` is what stops it yanking a choice
  // out from under someone -- the same guard `nameEdited` already provides for
  // the name, and the reason this can depend on `suggested` safely.
  useEffect(() => {
    if (!show || typeEdited) return;
    setLogbookType(suggested);
    if (!nameEdited) setName(defaultNameForType(suggested));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, suggested]);

  function typeChanged(e) {
    const id = e.target.value;
    setTypeEdited(true);
    setLogbookType(id);
    if (!nameEdited) {
      setName(defaultNameForType(id));
    }
  }

  function nameChanged(e) {
    setNameEdited(true);
    setName(e.target.value);
  }

  async function confirm() {
    setBusy(true);
    try {
      await onAdd({ logbookType, name: name.trim() || defaultNameForType(logbookType) });
    } finally {
      setBusy(false);
    }
  }

  function pickFile() {
    setImportError(null);
    fileInputRef.current?.click();
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again still fires `change`.
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      setPendingImport(parseLogbookExport(text));
      setImportError(null);
    } catch (err) {
      setImportError(extractErrorText(err));
      setPendingImport(null);
    }
  }

  async function confirmImport() {
    setBusy(true);
    setImportError(null);
    setImportProgress({ done: 0, total: pendingImport.entries.length, phase: 'entries' });
    try {
      // On success the parent navigates to the new logbook and this modal
      // unmounts, so we deliberately don't touch state afterwards.
      await onImport(pendingImport, (done, total, phase = 'entries') =>
        setImportProgress({ done, total, phase })
      );
    } catch (err) {
      setImportError(extractErrorText(err));
      setImportProgress(null);
      setBusy(false);
    }
  }

  // Reset whenever the modal is dismissed: reopening it is a fresh "add a
  // logbook", so the type and name suggestions should apply again rather than
  // carrying over a previous, abandoned attempt.
  function handleHide() {
    setPendingImport(null);
    setImportError(null);
    setImportProgress(null);
    setTypeEdited(false);
    setNameEdited(false);
    onHide();
  }

  const entryCount = pendingImport?.entries.length ?? 0;

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>{pendingImport ? 'Import Logbook' : 'Add Logbook'}</Modal.Title>
      </Modal.Header>

      {pendingImport ? (
        <>
          <Modal.Body>
            {importProgress ? (
              <>
                <ProgressBar
                  now={importProgress.total ? (importProgress.done / importProgress.total) * 100 : 100}
                  animated
                />
                <p className="text-center mb-0 mt-2" role="status" aria-live="polite">
                  {(PHASE_COPY[importProgress.phase] ?? PHASE_COPY.entries)(importProgress)}
                </p>
              </>
            ) : (
              <p className="mb-0">
                {`Import ${entryCount} ${entryWord(entryCount)} as a new "${pendingImport.logbook.name}" logbook?`}
              </p>
            )}
            {importError && (
              <p className="text-danger small mb-0 mt-3" role="alert">{importError}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setPendingImport(null)} disabled={busy}>Back</Button>
            <Button variant="success" onClick={confirmImport} disabled={busy}>Import</Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select value={logbookType} onChange={typeChanged} aria-label="Logbook type">
                  {LOGBOOK_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" value={name} onChange={nameChanged} aria-label="Logbook name" />
              </Form.Group>
            </Form>
            {importError && (
              <p className="text-danger small mb-0" role="alert">{importError}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="d-none"
              aria-label="Import logbook JSON file"
              onChange={handleFile}
            />
            <Button variant="outline-primary" className="me-auto" onClick={pickFile} disabled={busy}>
              Import from file
            </Button>
            <Button variant="secondary" onClick={handleHide} disabled={busy}>Cancel</Button>
            <Button variant="success" onClick={confirm} disabled={busy}>Add</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export const ItemLogbooks = ({ item, setItem }) => {
  const navigate = useNavigate();
  const [StatusMessage, { showText, showError, hideStatus }] = useStatusMessage();
  const [showAdd, setShowAdd] = useState(false);
  // The gazetteer, for the entry rows' place names.
  const placeIndex = usePlaceIndex();

  const logbooks = item?.logbooks?.items ?? [];

  // Persist model changes by cloning the item so <Item> re-renders.
  function commit() {
    setItem({ ...item });
  }

  async function addLogbook({ logbookType, name }) {
    try {
      showText('Adding logbook...');
      const created = await Database.createLogbook({ itemId: item.id, name, logbookType });
      created.completedTasks = { items: [] };
      if (!item.logbooks) {
        item.logbooks = { items: [] };
      }
      item.logbooks.items = [...item.logbooks.items, created];
      commit();
      hideStatus();
      setShowAdd(false);
    } catch (err) {
      showError(err);
    }
  }

  // Import a whole logbook (from a parsed export) as a brand-new logbook on this
  // item, then jump to it. `onProgress` drives the modal's counter. Rethrows so
  // the modal can show the failure inline (the page status is hidden behind the
  // open modal).
  async function importLogbook(parsed, onProgress) {
    showText('Importing logbook...');
    try {
      // The gazetteer rows the file's routes refer to, first: create-only, so
      // a name this environment already has is left exactly as it is. The
      // index it returns is what binds the imported routes to rows that exist
      // HERE rather than to the ids of the environment that wrote the file.
      //
      // Deliberately not fatal. The entries are the user's data and the place
      // rows are a bonus, so a failure is reported afterwards and the import
      // carries on -- the routes still draw from their own snapshots, and
      // scripts/backfill-places.mjs can fill the gap later.
      const { failed, index } = await ensurePlaces(parsed.places, { onProgress });

      const created = await Database.importLogbook(item.id, parsed, {
        onProgress,
        placeIndex: index,
      });
      hideStatus();
      setShowAdd(false);
      // The notice rides along rather than showing here: this page is about to
      // unmount, and the warning belongs next to the logbook it is about.
      navigate(urlForLogbookForId(item.id, created.id), {
        state: { notice: placesFailedNotice(failed) },
      });
    } catch (err) {
      hideStatus();
      throw err;
    }
  }

  return (
    <div>
      <StatusMessage />

      {logbooks.length === 0 && (
        <DataCard
          header={<CardHeader text="Logbooks" addText="Add Logbook" addTapped={() => setShowAdd(true)} />}
          body={
            <p className="text-center text-body-secondary m-0">
              {/* A log-only item's copy skips "maintenance": the user chose
                  trips-and-logs-only, so pitching the surface they turned off
                  reads like the choice didn't take. Gated on tracking (the
                  gating axis), never on itemType. */}
              {isLogOnly(item)
                ? "No logbooks yet. Add a Captain's Log to start recording trips."
                : 'No logbooks yet. Add one to start tracking maintenance, trips, or flights.'}
            </p>
          }
        />
      )}

      {logbooks.map((logbook) => {
        const cb = makeLogbookSummaryCallbacks({
          logbook,
          commit,
          onAll: () => navigate(urlForLogbookForId(item.id, logbook.id)),
        });
        return (
          <LogbookSummary
            key={logbook.id}
            item={item}
            logbook={logbook}
            placeIndex={placeIndex}
            onAll={cb.onAll}
            onAdded={cb.onAdded}
            onSaved={cb.onSaved}
            onDelete={cb.onDelete}
            // Saving a Captain's Log entry can move the item's counter (see
            // itemCounterSync.mjs); splice the new value in so the HourMeter
            // beside these cards repaints without a refetch.
            onCounterSaved={(counter) => {
              const items = item?.itemCounter?.items;
              if (!items) return;
              item.itemCounter.items = items.map((c) => (c.id === counter.id ? counter : c));
              commit();
            }}
          />
        );
      })}

      {logbooks.length > 0 && (
        <div className="text-center my-2">
          <Button variant="success" onClick={() => setShowAdd(true)}>
            <i className="fa-solid fa-plus me-1" aria-hidden="true"></i>
            Add Logbook
          </Button>
        </div>
      )}

      <AddLogbookModal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        onAdd={addLogbook}
        onImport={importLogbook}
        itemType={item?.itemType}
      />
    </div>
  );
};

export default ItemLogbooks;
